import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomActionButton, {atomActionButtonSizes, atomActionButtonStyles} from '@s-ui/react-atom-action-button'
import AtomButton, {atomButtonDesigns, atomButtonShapes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES, atomIconSizes} from '@s-ui/react-atom-icon'
import {inputSizes} from '@s-ui/react-atom-input'
import Context from '@s-ui/react-context'
import MoleculeInputField from '@s-ui/react-molecule-input-field'
import ModalBasic, {moleculeModalSizes} from '@adv-mt/mt-modal-basic'
import IconChatGPT from '@adv-mt/mt-svg-icons/lib/ChatGPT'
import CopyOutline from '@adv-mt/mt-svg-icons/lib/CopyOutline'
import IconFacebook from '@adv-mt/mt-svg-icons/lib/Facebook'
import IconMailOutline from '@adv-mt/mt-svg-icons/lib/MailOutline'
import IconPerplexity from '@adv-mt/mt-svg-icons/lib/Perplexity'
import IconWhatsApp from '@adv-mt/mt-svg-icons/lib/Whatsapp'
import IconXTwitter from '@adv-mt/mt-svg-icons/lib/XTwitter'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const COPY_CLIPBOARD_TIMEOUT = 3000
const NO_OP = () => {}

export const MODAL_SHARE_CONTEXTS = {
  DEFAULT: 'default',
  NEWS: 'news',
  AD_DETAIL: 'ad-detail'
}

const AI_ENABLED_CONTEXTS = [MODAL_SHARE_CONTEXTS.NEWS, MODAL_SHARE_CONTEXTS.AD_DETAIL]

const BASE_CLASS = 'mt-ModalShare'
const TITLE_CLASS = `${BASE_CLASS}Title`
const PROVIDERS_CLASS = `${BASE_CLASS}Providers`
const PROVIDER_CLASS = `${BASE_CLASS}Provider`
const COPY_CLASS = `${BASE_CLASS}Copy`
const CLIPBOARD_SHARE_ID = 'clipboard-share'

export default function ModalShare({
  isOpen,
  onClose,
  onShareClick,
  onCopyUrlClick = NO_OP,
  subtitle,
  title,
  urlToShare,
  shareContext = MODAL_SHARE_CONTEXTS.DEFAULT
}) {
  const {i18n, domain} = useContext(Context)
  const [isUrlCopied, setIsUrlCopied] = useState(false)
  const config = domain.get('config')
  const {SHARE} = config.get('COMMON')
  const {
    SHARE_METHOD_ID_EMAIL,
    SHARE_METHOD_ID_FACEBOOK,
    SHARE_METHOD_ID_TWITTER,
    SHARE_METHOD_ID_WHATSAPP,
    SHARE_METHOD_ID_CHATGPT,
    SHARE_METHOD_ID_PERPLEXITY
  } = SHARE

  const handleCopy = () => {
    navigator.clipboard.writeText(urlToShare)
    setIsUrlCopied(true)
    setTimeout(() => setIsUrlCopied(false), COPY_CLIPBOARD_TIMEOUT)
    onCopyUrlClick()
  }
  const modalTitle = title || i18n.t('COMMON.AD_SHARE')
  const modalSubtitle = subtitle || i18n.t('COMMON.AD_SHARE_METHODS_HEADER')

  const titleId = 'modal-share'

  const baseShareActions = [
    {
      action: SHARE_METHOD_ID_EMAIL,
      icon: IconMailOutline,
      text: 'SHARE_PROVIDERS.EMAIL'
    },
    {
      action: SHARE_METHOD_ID_WHATSAPP,
      icon: IconWhatsApp,
      text: 'SHARE_PROVIDERS.WHATSAPP'
    },
    {
      action: SHARE_METHOD_ID_FACEBOOK,
      icon: IconFacebook,
      text: 'SHARE_PROVIDERS.FACEBOOK'
    },
    {
      action: SHARE_METHOD_ID_TWITTER,
      icon: IconXTwitter,
      text: 'SHARE_PROVIDERS.TWITTER'
    }
  ]

  const getAIShareActions = () => {
    if (!AI_ENABLED_CONTEXTS.includes(shareContext)) return []

    return [
      {
        action: SHARE_METHOD_ID_CHATGPT,
        icon: IconChatGPT,
        text: 'SHARE_PROVIDERS.CHATGPT',
        isAI: true
      },
      {
        action: SHARE_METHOD_ID_PERPLEXITY,
        icon: IconPerplexity,
        text: 'SHARE_PROVIDERS.PERPLEXITY',
        isAI: true
      }
    ]
  }

  const shareActions = [...baseShareActions, ...getAIShareActions()]

  const getAIPrompt = () => {
    const promptKeys = {
      [MODAL_SHARE_CONTEXTS.NEWS]: 'SHARE_AI.NEWS_PROMPT',
      [MODAL_SHARE_CONTEXTS.AD_DETAIL]: 'SHARE_AI.AD_DETAIL_PROMPT'
    }

    const promptKey = promptKeys[shareContext]
    if (!promptKey) return ''

    const template = i18n.t(promptKey)
    return template.replace('%{url}', urlToShare)
  }

  const handleShareClick = ({action, isAI}) => {
    if (isAI) {
      const prompt = getAIPrompt()
      onShareClick({provider: action, prompt})
    } else {
      onShareClick({provider: action})
    }
  }

  return (
    <ModalBasic isInsetScrollArea isOpen={isOpen} onClose={onClose} size={moleculeModalSizes.MEDIUM} title={modalTitle}>
      <div className={BASE_CLASS}>
        <TitleBasic
          as={titleBasicTags.h4}
          id={titleId}
          className={TITLE_CLASS}
          size={titleBasicSizes.s}
          title={modalSubtitle}
        />
        <div className={`${PROVIDERS_CLASS}`}>
          {shareActions.map(({action, text, icon: Icon, isAI}) => (
            <AtomActionButton
              style={atomActionButtonStyles.OUTLINE}
              size={atomActionButtonSizes.LARGE}
              icon={<Icon className={`${PROVIDER_CLASS}Icon`} size={ATOM_ICON_SIZES.medium} />}
              onClick={() => handleShareClick({action, isAI})}
            >
              <span className={`${PROVIDER_CLASS}`}>{i18n.t(text)}</span>
            </AtomActionButton>
          ))}
        </div>
        <div className={`${COPY_CLASS}`}>
          <MoleculeInputField
            id={CLIPBOARD_SHARE_ID}
            value={urlToShare}
            label="Copiar URL"
            readOnly
            size={inputSizes.LARGE}
          />
          <AtomButton
            design={atomButtonDesigns.SOLID}
            shape={atomButtonShapes.CIRCULAR}
            leftIcon={<CopyOutline size={atomIconSizes.medium} />}
            onClick={handleCopy}
            size={moleculeModalSizes.LARGE}
          >
            {isUrlCopied ? i18n.t('COMMON.COPIED') : i18n.t('COMMON.COPY')}
          </AtomButton>
        </div>
      </div>
    </ModalBasic>
  )
}

ModalShare.displayName = 'ModalShare'
ModalShare.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  urlToShare: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  onCopyUrlClick: PropTypes.func,
  onShareClick: PropTypes.func,
  shareContext: PropTypes.oneOf(Object.values(MODAL_SHARE_CONTEXTS))
}
